import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { ChartBar, MapPin, ArrowsLeftRight, Tag, UserFocus, WarningCircle, Clock, DownloadSimple, Spinner, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

const MONEY = new Set(["total_value", "avg_price"]);
const fmtCell = (h, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return "—";
  if (MONEY.has(h)) return `$${Number(v).toLocaleString()}`;
  if (typeof v === "number") return v.toLocaleString();
  return v.toString().slice(0, 60);
};

const REPORTS = [
  { key: "inventory", label: "Inventory Report", desc: "Every asset with full details", icon: ChartBar },
  { key: "location", label: "Location Report", desc: "Asset counts per location", icon: MapPin },
  { key: "lot-breakdown", label: "Lot Breakdown", desc: "Vehicles & total value per lot — built for multi-location groups", icon: Buildings },
  { key: "movement", label: "Movement Report", desc: "Complete movement ledger", icon: ArrowsLeftRight },
  { key: "status", label: "Status Report", desc: "Assets grouped by status", icon: Tag },
  { key: "employee-activity", label: "Employee Activity", desc: "Who did what, and when", icon: UserFocus },
  { key: "missing", label: "Missing Asset Report", desc: "Assets flagged as missing", icon: WarningCircle },
  { key: "recently-added", label: "Recently Added", desc: "Newest assets first", icon: Clock },
];

export default function Reports() {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (key) => {
    setActive(key); setLoading(true); setData(null);
    try { const { data } = await api.get(`/reports/${key}`); setData(data); }
    catch { toast.error("Could not load report"); setActive(null); }
    setLoading(false);
  };

  const exportCsv = async (key) => {
    try {
      const res = await api.get(`/reports/${key}`, { params: { format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${key}-report.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Could not export report"); }
  };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div><h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Reports</h1><p className="text-zinc-500 text-sm mt-1">Generate and export operational reports.</p></div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORTS.map((r) => (
          <div key={r.key} className={`p-5 rounded-lg border bg-white ${active === r.key ? "border-emerald-500 ring-1 ring-emerald-500" : "border-zinc-200"}`} data-testid={`report-card-${r.key}`}>
            <div className="h-10 w-10 rounded-md bg-emerald-50 flex items-center justify-center mb-3"><r.icon size={20} weight="bold" className="text-emerald-600" /></div>
            <h3 className="font-display font-bold">{r.label}</h3>
            <p className="text-sm text-zinc-500 mt-0.5 mb-4">{r.desc}</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => run(r.key)} className="h-10 bg-zinc-950 hover:bg-zinc-800 text-white" data-testid={`run-${r.key}`}>View</Button>
              <Button size="sm" variant="outline" onClick={() => exportCsv(r.key)} className="h-10 gap-1.5 border-zinc-300" data-testid={`export-${r.key}`}><DownloadSimple size={16} /> CSV</Button>
            </div>
          </div>
        ))}
      </div>

      {active && (
        <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto" data-testid="report-result">
          {loading ? <div className="flex justify-center py-16"><Spinner size={28} className="animate-spin text-emerald-600" /></div> : data && (
            data.rows.length === 0 ? <p className="text-center py-12 text-zinc-400">No data for this report.</p> : (
              <Table>
                <TableHeader><TableRow className="bg-zinc-50">{data.headers.map((h) => <TableHead key={h} className="capitalize">{h.replace(/_/g, " ")}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {data.rows.map((row, i) => (
                    <TableRow key={i} className={`${row._total ? "bg-zinc-50 font-bold" : ""} ${row.location_id ? "cursor-pointer hover:bg-emerald-50/60" : ""}`} onClick={() => row.location_id && navigate(`/app/inventory?location_id=${row.location_id}`)} title={row.location_id ? "View this lot's inventory" : undefined} data-testid={`report-row-${i}`}>{data.headers.map((h) => <TableCell key={h} className="text-sm">{fmtCell(h, row[h])}</TableCell>)}</TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          )}
        </div>
      )}
    </div>
  );
}
